import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FilePlus2, LayoutTemplate, Lock, Trash2 } from "lucide-react";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { api } from "../../api/client";
import { formatMoney } from "../../lib/money";
import { StatusBadge } from "../../components/Badge";
import { useToast } from "../../components/Toast";
import type { Paginated, QuoteSummary, TemplateSummary } from "../../api/types";

// 範本清單。內建的「會議活動製作標準七類」是業主原始 .xls 的直接數位化,
// **不可刪除,但那顆鈕還是畫出來**(停用 + 寫明原因)—— 把它藏起來的話,
// 那一列看起來像壞掉的,而不是「這一份是系統的」。
//
// 自訂的刪得掉(業主 2026-09-17:「追加系統功能 範本刪除功能!!」)。
// 後端的 DELETE 一直都在,缺的只是這顆鈕 —— 而說明文件早就寫著
// 「自訂的可以刪,到『範本』那一頁刪」。**文件承諾了一個按不到的功能**,
// 他照著去找,找不到;那比沒寫更糟。
//
// 每一列的「套用到…」是這一頁的主要動作:選一張現有的報價單,或直接開一張新單。
// 真正的選項(取代 / 附加、要不要帶單價)都在編輯器的「套用範本」對話框裡問,
// 這一頁只負責把使用者連同選好的範本送到那張單上 —— 套用會蓋掉內容,
// 不該在清單頁一鍵發生。

/** 「套用到…」列出幾張最近的報價單。 */
const QUOTE_PICKER_SIZE = 20;

export function TemplateList() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<TemplateSummary[]>("/templates"),
  });

  // 正在挑「套用到哪一張」的範本;null 代表沒開對話框。
  const [target, setTarget] = useState<TemplateSummary | null>(null);
  /** 正在問「真的要刪嗎」的那一份;null 代表沒問。 */
  const [killing, setKilling] = useState<TemplateSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const cache = useQueryClient();

  const templates = Array.isArray(data) ? data : [];

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <h2 className="m-0 text-xl font-semibold text-[var(--text)]">範本</h2>
      <p className="m-0 text-sm text-[var(--text-2)]">
        用每一列的「套用到…」把範本套進某一張報價單,或直接開一張新單再套;
        也可以把手上這一單另存成範本。
      </p>

      {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          範本載入失敗,請稍後再試。
        </p>
      ) : null}

      {templates.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {templates.map((template) => (
            <li
              key={template.id}
              className="flex flex-wrap items-center gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2.5"
            >
              <LayoutTemplate aria-hidden="true" size={16} className="shrink-0 text-[var(--accent)]" />
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-[var(--text)]">{template.name}</span>
                {template.description ? (
                  <span className="block text-sm text-[var(--text-2)]">{template.description}</span>
                ) : null}
              </span>
              {template.is_system ? (
                <span className="flex items-center gap-1 rounded-[var(--radius)] bg-[var(--surface-2)] px-2 py-0.5 text-xs text-[var(--text-2)]">
                  <Lock aria-hidden="true" size={12} />
                  內建
                </span>
              ) : null}
              <span className="tabular shrink-0 text-sm text-[var(--text-2)]">
                {template.section_count} 章 · {template.line_count} 列
              </span>
              <Button
                size="sm"
                // 每一列都有一顆同名的按鈕,所以名稱要帶上範本名,不然讀螢幕的人分不出按到哪一列。
                aria-label={`把「${template.name}」套用到…`}
                onClick={() => setTarget(template)}
              >
                套用到…
              </Button>
              <Button
                size="sm"
                variant="danger"
                icon={<Trash2 aria-hidden="true" size={14} />}
                disabled={template.is_system}
                title={
                  template.is_system
                    ? "內建範本是系統的一部分,刪不掉"
                    : undefined
                }
                onClick={() => setKilling(template)}
              >
                刪除
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {!isLoading && !error && templates.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">還沒有任何範本。</p>
      ) : null}

      {/* **先問一次。** 一排長得很像的名字很容易按錯列 —— 雖然第 24 輪起
          刪掉的範本七天內救得回來(回收桶),但要他自己發現按錯了才算數。 */}
      <Dialog
        open={killing !== null}
        onClose={() => setKilling(null)}
        title="刪除範本"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setKilling(null)}>取消</Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={() => {
                const doomed = killing;
                if (!doomed) return;
                setBusy(true);
                void api
                  .del(`/templates/${doomed.id}`)
                  .then(() => {
                    toast.push(`已刪除「${doomed.name}」`, "success");
                    setKilling(null);
                    return cache.invalidateQueries({ queryKey: ["templates"] });
                  })
                  .catch((err: unknown) =>
                    toast.push(err instanceof Error ? err.message : "刪不掉", "error"),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              確定刪除
            </Button>
          </div>
        }
      >
        {/* **寫出是哪一個。** 只說「確定要刪嗎」的話,按錯列的人按下去才知道。 */}
        <p className="m-0 text-sm text-[var(--text)]">
          要刪掉「{killing?.name}」嗎?這一份有 {killing?.section_count} 章、
          {killing?.line_count} 列。
        </p>
        <p className="m-0 text-sm text-[var(--text-3)]">
          刪掉之後會放進<strong>回收桶七天</strong>,期間隨時還原得回來。
          已經套用過它的報價單不受影響 —— 套用是把內容複製過去的。
        </p>
      </Dialog>

      <ApplyTargetDialog
        template={target}
        onClose={() => setTarget(null)}
        onPickQuote={(quoteId, templateId) => navigate(`/quotes/${quoteId}?apply_template=${templateId}`)}
        onPickNew={(templateId) => navigate(`/quotes/new?template=${templateId}`)}
      />
    </div>
  );
}

interface ApplyTargetDialogProps {
  /** null 時不顯示。 */
  template: TemplateSummary | null;
  onClose: () => void;
  onPickQuote: (quoteId: number, templateId: number) => void;
  onPickNew: (templateId: number) => void;
}

/** 「套用到哪一張報價單?」清單。刻意只列最近的幾張,要更早的用搜尋或從清單頁進去。 */
function ApplyTargetDialog({ template, onClose, onPickQuote, onPickNew }: ApplyTargetDialogProps) {
  const [quotes, setQuotes] = useState<QuoteSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const open = template !== null;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .get<Paginated<QuoteSummary>>("/quotes", { page: 1, size: QUOTE_PICKER_SIZE })
      .then((result) => {
        if (cancelled) return;
        setQuotes(Array.isArray(result?.items) ? result.items : []);
        setFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setQuotes([]);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!template) return null;

  return (
    <Dialog open={open} onClose={onClose} title="套用到哪一張報價單?">
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm text-[var(--text-2)]">
          範本:{template.name}({template.section_count} 章 · {template.line_count} 列)
        </p>

        <Button
          variant="primary"
          icon={<FilePlus2 aria-hidden="true" size={16} />}
          onClick={() => onPickNew(template.id)}
        >
          開新單套用
        </Button>

        {loading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
        {failed ? (
          <p role="alert" className="m-0 text-sm text-[var(--danger)]">
            報價單清單載入失敗,請稍後再試。
          </p>
        ) : null}
        {!loading && !failed && quotes.length === 0 ? (
          <p className="m-0 text-sm text-[var(--text-3)]">還沒有任何報價單,請用上面的「開新單套用」。</p>
        ) : null}

        {quotes.length > 0 ? (
          <ul aria-label="最近的報價單" className="m-0 flex max-h-72 list-none flex-col gap-1 overflow-y-auto p-0">
            {quotes.map((quote) => (
              <li key={quote.id} className="m-0">
                <button
                  type="button"
                  onClick={() => onPickQuote(quote.id, template.id)}
                  className="flex w-full items-center gap-2 rounded-[var(--radius)] border border-[var(--border)] px-2.5 py-2 text-left hover:bg-[var(--surface-2)]"
                >
                  <span className="tabular shrink-0 text-sm font-medium text-[var(--accent)]">{quote.quote_no}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--text)]">{quote.title}</span>
                  <StatusBadge status={quote.status} />
                  <span className="tabular shrink-0 text-sm text-[var(--text-2)]">{formatMoney(quote.total)}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Dialog>
  );
}
